import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Home, CalendarDays, Layers, Database, Users, BookOpen, GraduationCap,
  Search, BarChart3, Wallet, ListChecks, Activity, FileSearch, Sparkles,
  ArrowRight, ArrowLeft,
} from 'lucide-react';

const ICONS = {
  home: Home, week: CalendarDays, projects: Layers, master: Database,
  team: Users, knowledge: BookOpen, guide: GraduationCap, nav: Search,
  reports: BarChart3, finance: Wallet, milestones: ListChecks,
  workload: Activity, sop: FileSearch,
};

/** TourDialog — step-by-step What's New walkthrough for a release. */
export function TourDialog({ release, open, onClose }) {
  const [step, setStep] = useState(0);
  useEffect(() => { if (open) setStep(0); }, [open]);
  if (!release) return null;
  const items = release.highlights || [];
  const h = items[step];
  const Icon = ICONS[h?.icon] || Sparkles;
  const last = step === items.length - 1;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[480px]" data-testid="whats-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-[hsl(var(--primary))]" />
            What&rsquo;s new · {release.title}
          </DialogTitle>
          <DialogDescription>{release.tagline}</DialogDescription>
        </DialogHeader>

        {h && (
          <div className="rounded-xl border p-5 text-center"
               style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--surface-1))' }}
               data-testid={`whats-new-step-${step}`}>
            <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3"
                 style={{ background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))' }}>
              <Icon size={22} />
            </div>
            <div className="font-heading font-semibold text-[16px]">{h.title}</div>
            <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1.5 leading-relaxed">{h.body}</div>
          </div>
        )}

        <div className="flex items-center justify-center gap-1.5" data-testid="whats-new-dots">
          {items.map((_, i) => (
            <button key={i} onClick={() => setStep(i)}
                    className="w-2 h-2 rounded-full transition-all"
                    style={{ background: i === step ? 'hsl(var(--primary))' : 'hsl(var(--surface-2))',
                             transform: i === step ? 'scale(1.3)' : 'none' }}
                    aria-label={`Step ${i + 1}`} />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button onClick={onClose} className="text-[12.5px] text-[hsl(var(--foreground-subtle))] hover:underline"
                  data-testid="whats-new-skip">
            Skip tour
          </button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} data-testid="whats-new-back">
                <ArrowLeft size={13} className="mr-1" />Back
              </Button>
            )}
            {last ? (
              <Button size="sm" onClick={onClose} data-testid="whats-new-finish">
                Get started
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep(s => s + 1)} data-testid="whats-new-next">
                Next<ArrowRight size={13} className="ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * WhatsNewAutoPrompt — mounted once in AppShell. On first login after a
 * release, fetches the latest What's New and shows the tour until acked.
 */
export function WhatsNewAutoPrompt() {
  const [release, setRelease] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('32d.wn.checked')) return;
    sessionStorage.setItem('32d.wn.checked', '1');
    api.get('/releases/whats-new').then(r => {
      if (r.data?.available && !r.data.seen) {
        setRelease(r.data);
        setOpen(true);
      }
    }).catch(() => {});
  }, []);

  const dismiss = async () => {
    setOpen(false);
    try { await api.post('/releases/whats-new/ack', { version: release.version }); } catch {}
  };

  if (!release) return null;
  return <TourDialog release={release} open={open} onClose={dismiss} />;
}
